import { api } from './client';
import type { Processo, Artefato, PaginatedResponse, PaginationParams } from './client';
import type { TagResumo } from './tags';

/** Lê o campo de anotações do cliente (campo com nome sem acento na API Python) */
export function getClienteNotes(c: Cliente): string {
  return (c as unknown as Record<string, unknown>)['observac' + 'oes'] as string ?? '';
}

/** Define o campo de anotações no payload */
export function setNotesPayload(payload: Record<string, unknown>, value: string | undefined): void {
  payload['observac' + 'oes'] = value;
}

// Constantes de enum tipo_pessoa
export const TIPO_PF = 'FISICA';
export const TIPO_PJ = 'JURIDICA';

// --- Tipos ---

export interface Cliente {
  id: string;
  usuario_id: string;
  escritorio_id?: string | null;
  escritorio_nome?: string | null;
  origem?: string;
  /** Quem compartilhou, quando `origem === 'compartilhado'` (visão do escritório). */
  compartilhado_por?: string | null;
  tipo_pessoa: string;
  nome: string;
  cpf?: string | null;
  rg?: string | null;
  razao_social?: string | null;
  cnpj?: string | null;
  email?: string | null;
  telefone?: string | null;
  telefone2?: string | null;
  endereco_logradouro?: string | null;
  endereco_numero?: string | null;
  endereco_complemento?: string | null;
  endereco_bairro?: string | null;
  endereco_cidade?: string | null;
  endereco_uf?: string | null;
  endereco_cep?: string | null;
  profissao?: string | null;
  estado_civil?: string | null;
  data_nascimento?: string | null;
  nacionalidade?: string | null;
  status: 'PROSPECTO' | 'ATIVO' | 'INATIVO';
  observacoes?: string | null;
  created_at: string;
  updated_at: string;
  /** Tags que quem consulta vê (PLANO_TAGS). */
  tags?: TagResumo[];
}

export interface ClienteResumo {
  id: string;
  nome: string;
  cpf_ou_cnpj: string;
  tipo_pessoa: string;
  status: 'PROSPECTO' | 'ATIVO' | 'INATIVO';
}

export interface ClienteCreate {
  tipo_pessoa: string;
  nome: string;
  cpf?: string;
  rg?: string;
  razao_social?: string;
  cnpj?: string;
  email?: string;
  telefone?: string;
  telefone2?: string;
  endereco_logradouro?: string;
  endereco_numero?: string;
  endereco_complemento?: string;
  endereco_bairro?: string;
  endereco_cidade?: string;
  endereco_uf?: string;
  endereco_cep?: string;
  profissao?: string;
  estado_civil?: string;
  data_nascimento?: string;
  nacionalidade?: string;
  status?: 'PROSPECTO' | 'ATIVO' | 'INATIVO';
  observacoes?: string;
  escritorio_id?: string;
}

export interface ClienteUpdate {
  tipo_pessoa?: 'PF' | 'PJ';
  nome?: string;
  cpf?: string;
  rg?: string;
  razao_social?: string;
  cnpj?: string;
  email?: string;
  telefone?: string;
  telefone2?: string;
  endereco_logradouro?: string;
  endereco_numero?: string;
  endereco_complemento?: string;
  endereco_bairro?: string;
  endereco_cidade?: string;
  endereco_uf?: string;
  endereco_cep?: string;
  profissao?: string;
  estado_civil?: string;
  data_nascimento?: string;
  nacionalidade?: string;
  status?: 'PROSPECTO' | 'ATIVO' | 'INATIVO';
  observacoes?: string;
  escritorio_id?: string | null;
}

export interface ClienteCounts {
  total: number;
  prospecto: number;
  ativo: number;
  inativo: number;
}

export interface PeticaoInicialResumo {
  id: string;
  tribunal_nome: string;
  classe_processual_nome: string;
  status: string;
  created_at: string;
}

// --- Parâmetros de listagem ---

interface FetchClientesParams extends PaginationParams {
  search?: string;
  /** Rigor da busca inteligente no servidor: `strict` exige a palavra inteira. */
  rigor?: 'flexible' | 'strict';
  status?: string;
  tipo_pessoa?: string;
  origem?: string;
  /** Filtro facetado por tags (PLANO_TAGS D7), aplicado no servidor. */
  tag_ids?: string[];
}

// --- Funções ---

export async function fetchClientes(params?: FetchClientesParams): Promise<PaginatedResponse<Cliente>> {
  const { data } = await api.get<PaginatedResponse<Cliente>>('/clientes', { params });
  return data;
}

export async function fetchClienteCounts(params?: {
  search?: string;
  rigor?: 'flexible' | 'strict';
  status?: string;
  origem?: string;
  tag_ids?: string[];
}): Promise<ClienteCounts> {
  const { data } = await api.get<ClienteCounts>('/clientes/counts', { params });
  return data;
}

export async function searchClientes(q: string): Promise<ClienteResumo[]> {
  const { data } = await api.get<ClienteResumo[]>('/clientes/search', { params: { q } });
  return data;
}

export async function fetchCliente(id: string): Promise<Cliente> {
  const { data } = await api.get<Cliente>(`/clientes/${id}`);
  return data;
}

export async function createCliente(payload: ClienteCreate): Promise<Cliente> {
  const { data } = await api.post<Cliente>('/clientes', payload);
  return data;
}

export async function updateCliente(id: string, payload: ClienteUpdate): Promise<Cliente> {
  const { data } = await api.put<Cliente>(`/clientes/${id}`, payload);
  return data;
}

export async function deleteCliente(id: string): Promise<void> {
  await api.delete(`/clientes/${id}`);
}

/** Item da lista de processos do cliente — Processo + discriminadores extras. */
export interface ClienteProcesso extends Processo {
  orgao_julgador?: string | null;
  /** Primeira parte do polo oposto ao do cliente + quantas mais existem. */
  parte_adversa?: { nome: string; excedente: number } | null;
  /** Nomes das partes (sem as ocultas), para o filtro rápido da aba. */
  partes?: string[];
}

export async function fetchClienteProcessos(id: string): Promise<ClienteProcesso[]> {
  const { data } = await api.get<ClienteProcesso[]>(`/clientes/${id}/processos`);
  return data;
}

/** Resultado do "Atualizar" da aba de processos (busca no Data Lake + vínculo local). */
export interface AtualizacaoProcessosCliente {
  /** Por que a busca no Data Lake não rodou (ou rodou pela metade); o vínculo local roda sempre. */
  busca: 'ok' | 'requer_pdpj' | 'cliente_sem_documento' | 'documento_invalido' | 'indisponivel' | 'recusada';
  novos: number;
  ja_cadastrados: number;
  acima_do_limite: number;
  partes_vinculadas: number;
  /** Processos que passaram a aparecer na aba (inclui os novos). */
  vinculados_na_aba: number;
  trabalhistas_buscados: boolean;
}

export async function atualizarProcessosCliente(id: string): Promise<AtualizacaoProcessosCliente> {
  const { data } = await api.post<AtualizacaoProcessosCliente>(`/clientes/${id}/atualizar-processos`);
  return data;
}

export async function fetchClientePeticoesIniciais(id: string): Promise<PeticaoInicialResumo[]> {
  const { data } = await api.get<PeticaoInicialResumo[]>(`/clientes/${id}/peticoes-iniciais`);
  return data;
}

export async function fetchClienteArtefatos(id: string): Promise<Artefato[]> {
  const { data } = await api.get<Artefato[]>(`/clientes/${id}/artefatos`);
  return data;
}

// --- Client Sync ---

export interface ClientSyncProgress {
  status: 'PENDING' | 'RUNNING' | 'COMPLETED' | 'FAILED' | 'CANCELLED' | 'idle';
  total_processos?: number;
  processos_sincronizados?: number;
  clientes_criados?: number;
  clientes_vinculados?: number;
  processos_nao_encontrados?: number;
  processos_em_carga?: number;
  processos_com_erro?: number;
  pct?: number;
  error?: string;
}

export async function triggerClientSync(): Promise<{ status: string; outbox_event_id?: string }> {
  const { data } = await api.post<{ status: string; outbox_event_id?: string }>('/clientes/sync-from-processos');
  return data;
}

export async function cancelClientSync(): Promise<{ status: string }> {
  const { data } = await api.post<{ status: string }>('/clientes/sync-cancel');
  return data;
}

export async function fetchClientSyncStatus(): Promise<ClientSyncProgress> {
  const { data } = await api.get<ClientSyncProgress>('/clientes/sync/status');
  return data;
}
